import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { einpassen, inCm, RAHMEN_TEXT } from "../shared/rahmen.ts";
import type { BildAusListe } from "./lib/api.ts";
import type { Reihenlayout } from "./lib/anordnung.ts";
import { scrollNachZoom, ZOOM_SCHRITT, zoomGrenzen } from "./lib/rahmen.ts";
import Figurbild from "./Figurbild.tsx";

/**
 * Schritt 7 aus `PROTOTYP-PLAN.md`: die Layout-Vorschau im Rahmen.
 *
 * Bis zum 24.09.2026 stand hier die ungerahmte Vorschau aus Schritt 4 — die Reihe auf
 * Behälterbreite verkleinert, ohne Rahmen, ohne Papier, ohne Zoom. Gemessen ergab das bei
 * 60 Figuren einen Streifen von **29 px Höhe**; sie belegte damit vor allem ihre eigene
 * Unzulänglichkeit (ARCHITEKTUR.md 6.2). Jetzt steht die Reihe im Rahmen **80 × 25 cm**
 * auf weißem Papier, und man kann hineinzoomen.
 *
 * Drei Festlegungen, die man im Bild nicht sieht:
 *
 * 1. **Keine sichtbare Bodenlinie.** Im Editor erklärt die durchgezogene Linie die Regel
 *    ohne Text. Auf Annas Blatt gibt es sie nicht — „reduzierter Stil: keine Gesichter,
 *    **kein Hintergrund**" (ihre Produktseite). Die Vorschau zeigt, was gedruckt wird.
 * 2. **Gesenkte Figuren werden an der Bodenlinie abgeschnitten**, genau wie im Editor.
 *    Das ist der Zweck des Versatzes: angeschnittene Beine verschwinden unter der Linie
 *    (6.2). Der spätere Export muss dasselbe tun — deshalb steht die Rechnung in
 *    `shared/rahmen.ts` und nicht hier.
 * 3. **Nicht anfassbar** (6.2). Hier wird verglichen, nicht bedient.
 *
 * Jeder Text ist deutsch (`CLAUDE.md`).
 */

interface Props {
  bilder: readonly BildAusListe[];
  /** Die fertige Reihe aus `reihenlayout()`. Gerechnet wird in `src/Reihe.tsx` — es soll
   *  eine Reihe geben und nicht zwei, die auseinanderlaufen können. */
  layout: Reihenlayout;
}

export default function Rahmenvorschau({ bilder, layout }: Props) {
  const [zoom, setZoom] = useState(1);
  const [rahmenBreite, setRahmenBreite] = useState(0);
  const behaelter = useRef<HTMLDivElement>(null);
  /** Der Stand **vor** der letzten Zoomänderung — die Grundlage, auf der der Mittelpunkt
   *  festgehalten wird. Steht in einer Referenz: ein Zustandswechsel je Reglerschritt
   *  würde alle 60 Figuren neu rendern (ARCHITEKTUR.md 3.1). */
  const vorher = useRef({ zoom: 1, links: 0, oben: 0, breite: 0, hoehe: 0 });

  // Gemessen und nicht angenommen: der Rahmen ist so breit wie der Platz, den er hat, und
  // beim Drehen des Telefons ändert sich das. Gemessen wird das **Randmaß** und nicht
  // `clientWidth` — sonst schrumpfte die Messung, sobald am Rechner ein Rollbalken
  // erscheint, und der schmalere Rahmen ließe den Rollbalken wieder verschwinden.
  useEffect(() => {
    const element = behaelter.current;
    if (!element) return;
    const messen = () => {
      // ⚠️ **Der Stand wird hier mitgeführt, nicht nur beim Reglerschieben** — vom
      // `step-review` am 24.09.2026 gefunden. Eine Drehung ändert die Rahmenbreite und
      // damit die Zoom-Obergrenze; steht der Zoom darüber, wird er beim Rendern
      // heruntergeklemmt, **ohne** dass `zoomAendern` läuft. Der Effekt unten rechnete
      // dann mit Fenstermaßen von vor der Drehung, und die Bildlaufposition sprang.
      // `zoom` bleibt stehen: es ist der Zoom **vor** dieser Änderung, und genau den
      // braucht die Rechnung.
      vorher.current = {
        zoom: vorher.current.zoom,
        links: element.scrollLeft,
        oben: element.scrollTop,
        breite: element.clientWidth,
        hoehe: element.clientHeight,
      };
      setRahmenBreite(element.getBoundingClientRect().width);
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => {
      beobachter.disconnect();
    };
  }, []);

  const einBasis = einpassen(layout.breite, layout.hoehe, rahmenBreite);
  const grenzen = zoomGrenzen(einBasis.faktor);
  // Eingesperrt beim Rendern statt in einem Effekt: die Obergrenze hängt an der gemessenen
  // Breite, und die ändert sich beim Drehen. Ein Zoom, der nach dem Drehen über seiner
  // Grenze steht, zeigte sonst einen Rahmen, der nicht mehr in den Behälter passt.
  const z = Math.min(Math.max(grenzen.min, zoom), grenzen.max);
  const ein = einpassen(layout.breite, layout.hoehe, rahmenBreite * z);

  // Nach jeder Zoomänderung den Mittelpunkt wiederherstellen. `useLayoutEffect`, weil das
  // vor dem Zeichnen passieren muss — sonst sieht man den Sprung an den Anfang der Reihe
  // und danach den Sprung zurück.
  useLayoutEffect(() => {
    const element = behaelter.current;
    if (!element) return;
    const alt = vorher.current;
    if (alt.zoom === z) return;
    element.scrollLeft = scrollNachZoom(
      alt.links,
      alt.breite,
      element.clientWidth,
      alt.zoom,
      z,
      element.scrollWidth - element.clientWidth,
    );
    element.scrollTop = scrollNachZoom(
      alt.oben,
      alt.hoehe,
      element.clientHeight,
      alt.zoom,
      z,
      element.scrollHeight - element.clientHeight,
    );
    vorher.current = {
      zoom: z,
      links: element.scrollLeft,
      oben: element.scrollTop,
      breite: element.clientWidth,
      hoehe: element.clientHeight,
    };
  }, [z]);

  function zoomAendern(wert: number) {
    const element = behaelter.current;
    if (element) {
      vorher.current = {
        zoom: z,
        links: element.scrollLeft,
        oben: element.scrollTop,
        breite: element.clientWidth,
        hoehe: element.clientHeight,
      };
    }
    setZoom(wert);
  }

  // Nachschlagen statt Durchsuchen: bei 60 Figuren wäre ein `find()` je Figur die
  // quadratische Schleife, die in der Vorschau niemand vermutet.
  const nachId = useMemo(() => new Map(bilder.map((bild) => [bild.bildId, bild])), [bilder]);

  const groessteFigur = layout.figuren.reduce((hoch, figur) => Math.max(hoch, figur.hoehe), 0);
  const figurCm = inCm(groessteFigur, einBasis.faktor, rahmenBreite);

  return (
    <div className="flex flex-col gap-2">
      {/* ⚠️ **Hier steht mit Absicht kein `touch-action`.** Eine Einschränkung auf
          `pan-x pan-y` nähme dem Browser seine eigene Zwei-Finger-Vergrößerung — und die
          ist die Antwort auf den bekannten Nachteil des Reglers: wer auf einem Telefon
          zuerst zum Pinch greift, vergrößert wenigstens die Seite und steht nicht vor
          einer Geste, die nichts tut.

          `overscroll-behavior` bleibt: das Wischen soll in der Vorschau hängen bleiben
          und nicht die Seite mitnehmen — dieselbe Falle wie bei der Leiste in Schritt 4. */}
      <div
        ref={behaelter}
        className="overflow-auto overscroll-contain"
        style={{
          height: ein.rahmenHoehe,
          // Beim Vergrößern wächst das Fenster mit, statt sofort zu scrollen: bei 36
          // Figuren ist der eingepasste Rahmen nur 112 px hoch, und in einem 112-px-Fenster
          // sieht man von einer vergrößerten Figur ein Drittel. Erst oberhalb dieser
          // Grenze wird senkrecht gescrollt.
          //
          // ⚠️ **Der eingepasste Rahmen steht in der Klammer, und das ist kein Zierrat:**
          // ohne ihn könnte der Deckel schon bei „Ganzes Blatt" greifen — auf einem sehr
          // flachen Fenster wäre 60 dvh kleiner als die Rahmenhöhe, und das Blatt, das
          // ganz zu sehen sein soll, wäre angeschnitten. Der Knopf hieße dann falsch.
          maxHeight: `max(${String(einBasis.rahmenHoehe)}px, 60dvh)`,
        }}
      >
        <div
          className="relative bg-white ring-1 ring-neutral-300"
          style={{ width: Math.max(rahmenBreite * z, 1), height: ein.rahmenHoehe }}
        >
          <div
            // Unten links verankert und von dort skaliert: der Fußpunkt der Skalierung
            // fällt mit der Bodenlinie zusammen — dieselbe Verankerung wie in der
            // ungerahmten Vorschau, und aus demselben Grund (im Fluss läge die Reihe
            // unterhalb der Unterkante und würde weggeschnitten, gesehen am 24.09.2026).
            //
            // `overflow-hidden` ist hier die **Bodenlinie**: was eine gesenkte Figur unter
            // die Linie schiebt, wird abgeschnitten. Ohne das stünde in der Vorschau ein
            // Bein, das der Editor verborgen hat.
            className="pointer-events-none absolute overflow-hidden"
            style={{
              left: ein.links,
              bottom: ein.unten,
              width: Math.max(layout.breite, 1),
              height: Math.max(layout.hoehe, 1),
              transform: `scale(${String(ein.faktor)})`,
              transformOrigin: "bottom left",
            }}
          >
            {layout.figuren.map((gelegt) => {
              const bild = nachId.get(gelegt.bildId);
              if (!bild) return null;
              return (
                <span
                  key={gelegt.bildId}
                  className="absolute"
                  style={{
                    left: gelegt.links,
                    bottom: gelegt.hebung,
                    width: gelegt.breite,
                    height: gelegt.hoehe,
                  }}
                >
                  <Figurbild bild={bild} />
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Der Regler erscheint nur, wenn es etwas zu vergrößern gibt. Bei wenigen Figuren
          füllt die Reihe die Rahmenhöhe bereits — ein Regler ohne Wirkung wäre schlimmer
          als keiner. */}
      {grenzen.max > grenzen.min && (
        <label className="flex flex-col gap-1 text-sm text-neutral-800">
          {/* Der Regler ist zugleich die tastaturtaugliche Form (ARCHITEKTUR.md 3.8):
              Pfeiltasten bewegen ihn, ohne dass es eine zweite Bedienung braucht. */}
          <span className="flex justify-between">
            <span>Ganzes Blatt</span>
            <span>Ganz nah</span>
          </span>
          <input
            type="range"
            min={grenzen.min}
            max={grenzen.max}
            step={ZOOM_SCHRITT}
            value={z}
            aria-label="Vergrößern"
            onChange={(event) => {
              zoomAendern(Number(event.target.value));
            }}
            className="min-h-11"
          />
        </label>
      )}

      {/* Die Zahl, die `TODO.md` offen führt: „Wie viele Figuren trägt 80 × 25 cm
          wirklich?". Sie steht dort, wo sie entsteht, und ändert sich mit jeder Figur.

          ⚠️ **„Deine Collage wird … groß" statt nur „80 × 25 cm"** — vom
          `usability-reviewer` am 24.09.2026 gefunden: ohne den Satzanfang liest sich die
          Zahl wie eine Angabe über das, was gerade auf dem Bildschirm steht. Gemeint ist
          das gemalte Bild, das Anna später schickt. „darauf" bindet die Figurenhöhe an das
          Blatt und nicht an die Vorschau.

          ⚠️ **„Das größte Bild" und „Deine Collage" in zwei Sätzen nebeneinander** — seit
          das Element vor der Kundin „Bild" heißt (`src/Reihe.tsx`, Dateikopf), liegen die
          beiden Bedeutungen dicht beieinander. Auseinander hält sie, dass der erste Satz
          „Collage" sagt und nicht „Bild", und dass „darauf" zurückzeigt. Steht die Kundin
          trotzdem davor und fragt, ist der erste Satz der, der umformuliert wird. */}
      <p className="text-sm text-neutral-600">
        Deine Collage wird {RAHMEN_TEXT} groß.
        {figurCm > 0 && ` Das größte Bild wird darauf rund ${cm(figurCm)} hoch.`}
      </p>
    </div>
  );
}

/** Eine Länge vor der Kundin: deutsches Komma, eine Nachkommastelle. „7.5 cm" wäre ein
 *  englischer Text in einer deutschen Oberfläche (`CLAUDE.md`). */
function cm(wert: number): string {
  return `${wert.toLocaleString("de-DE", { maximumFractionDigits: 1 })} cm`;
}
